import React from 'react';
import { useTranslation } from 'react-i18next';
import dynamic from 'next/dynamic';
import * as S from './MyAdsSection.styles';
import {
  SCROLL_GAP,
  SCROLL_STEP,
  SCROLL_THRESHOLD,
} from './MyAdsSection.constants';
import { Ads } from '@/features/Profile/sections/MyAds/components/Ads/Ads';
import { useMyAdsSection } from '@/features/Profile/sections/MyAds/hooks/useMyAdsSection';
import { ROUTES } from '@/constants/routes';
import { SuccessPaymentStatusModal } from '@/components/Payment/SuccessPaymentStatusModal';
import { DeleteAdModal } from '@/components/Ad/DeleteAdModal';
import { WarningModal } from '@/components/Ad/WarningModal';
import { Button } from '@/components/UI/Button/Button';
import { CommonTestIds } from '@/__e2e__/testIds/common';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { useToastStore } from '@/store/useToastStore';
import { ChevronDown, SuccessFilledIcon } from '@/components/UI/Icons/Icons';
import { useScroller } from '@/hooks/useScroller';
import { useResponsive } from '@/hooks/useResponsive';
import { AdStatus } from '@/types';
import { ShopAccountSummary } from '@/features/Profile/core';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { QUERY } from '@/constants/common';
import { MyAdsBulkPromotion } from '@/features/Profile/sections/MyAds/components/MyAdsBulkPromotion';
import { MyAdsBulkPromotionSuccessModal } from '@/features/Profile/sections/MyAds/components/MyAdsBulkPromotion/MyAdsBulkPromotionSuccessModal';
import { useMyAdsBulkPromotionReceipt } from '@/features/Profile/sections/MyAds/hooks';
import { ProfilePageTestIds } from '@/__e2e__/testIds/profile-page';
import {
  useCurrentUser,
  useScrollSelectedTabIntoView,
} from '@/features/Profile/core/hooks';
import { IconBox } from '@/components/UI/IconBox/IconBox.styles';

const MyAdsPromotionFlow = dynamic(
  () =>
    import('./components/MyAdsPromotionFlow').then(
      mod => mod.MyAdsPromotionFlow
    ),
  { ssr: false }
);

const MyAdsRestore = dynamic(
  () => import('./components/MyAdsRestore').then(mod => mod.MyAdsRestore),
  { ssr: false }
);

export const MyAdsSection = () => {
  const { t } = useTranslation();
  const { openToast } = useToastStore();
  const { isDesktop } = useResponsive();
  const {
    isActiveShop,
    isShop,
    shop,
    user,
    loading: userLoading,
  } = useCurrentUser();
  const [, toggleAddBalance] = useToggleOpenState(QUERY.replenishment);
  const {
    actionHandlers,
    adStatusCounts,
    archiveAd,
    canBulkPromote,
    canBulkRestore,
    closeBulkPromotion,
    closeArchiveAdFlow,
    closeEditPendingApproval,
    closePayByBalanceFlow,
    closeRemoveAdFlow,
    closePromotionFlow,
    closeRestore,
    confirmPayByBalance,
    deleteReasons,
    handleBulkPromotionSuccess,
    handlePromotionSuccess,
    handleRestoreSuccess,
    isAdsCountersLoading,
    isArchiveAdFlowOpen,
    isArchiveAdLoading,
    isBulkPromotionMode,
    isEditPendingApprovalOpen,
    isPayByBalanceLoading,
    isPayByBalanceOpen,
    isRemoveAdFlowOpen,
    isRestoreMode,
    openArchiveAdFlow,
    openBulkPromotion,
    openRestore,
    payByBalanceAd,
    promotionAd,
    removeAd,
    selectedAdStatus,
    shopPaidAdsBalance,
    tabItems,
  } = useMyAdsSection();
  const {
    closePaymentReceipt: closeBulkPromotionPaymentReceipt,
    completePayment: completeBulkPromotionPayment,
    paymentReceipt: bulkPromotionPaymentReceipt,
  } = useMyAdsBulkPromotionReceipt({
    onPaymentSuccess: handleBulkPromotionSuccess,
  });
  const tabsRef = useScrollSelectedTabIntoView([selectedAdStatus], {
    enabled: !isAdsCountersLoading,
  });
  const { isScrolled, isOverflowing, onScrollerClick } = useScroller(
    {
      containerRef: tabsRef,
      gap: SCROLL_GAP,
      step: SCROLL_STEP,
      threshold: SCROLL_THRESHOLD,
    },
    [isAdsCountersLoading, isDesktop, tabItems.length]
  );
  const hasScroller = isDesktop && isOverflowing;
  const isArchiveTabSelected = selectedAdStatus === AdStatus.Archived;

  function handleArchiveAdConfirm() {
    archiveAd().then(isArchived => {
      if (!isArchived) return;

      openToast({
        text: t('pages.cabinet.archive_ad_success'),
        icon: <SuccessFilledIcon width={20} height={20} />,
        size: 'compact',
        borderRadius: 14,
      });
    });
  }

  function handleRemoveAdConfirm(data: Parameters<typeof removeAd>[0]) {
    removeAd(data).then(isRemoved => {
      if (!isRemoved) return;

      openToast({
        text: t('pages.cabinet.remove_ad_success'),
        icon: <SuccessFilledIcon width={20} height={20} />,
        size: 'compact',
        borderRadius: 14,
      });
    });
  }

  return (
    <>
      {isShop && isDesktop && (
        <ShopAccountSummary
          balance={user.walletBalance ?? 0}
          isActiveShop={isActiveShop}
          isBalanceLoading={userLoading}
          isPendingInvoice={shop?.isPendingInvoice}
          onAddBalance={() => toggleAddBalance()}
        />
      )}

      <S.ContentCard>
        {isRestoreMode ? (
          <MyAdsRestore
            shopPaidAdsBalance={shopPaidAdsBalance}
            onClose={closeRestore}
            onRestored={handleRestoreSuccess}
          />
        ) : isBulkPromotionMode ? (
          <MyAdsBulkPromotion
            onClose={closeBulkPromotion}
            onPaymentSuccess={completeBulkPromotionPayment}
          />
        ) : (
          <>
            <S.TitleRow>
              <S.Title $variant="2">{t('pages.cabinet.my_ads')}</S.Title>
              {canBulkRestore && (
                <S.RestoreEntryButton
                  data-testid={CommonTestIds.adCards.restoreEntryButton}
                  onClick={openRestore}
                >
                  {t('common.restore')}
                </S.RestoreEntryButton>
              )}
              {canBulkPromote && (
                <S.BulkPromotionEntryButton
                  data-testid={ProfilePageTestIds.bulkPromotion.entryButton}
                  onClick={openBulkPromotion}
                >
                  <IconBox $filename="chart.svg" />
                  {t('common.advertise')}
                </S.BulkPromotionEntryButton>
              )}
            </S.TitleRow>
            <S.TabsWrapper>
              {hasScroller && isScrolled && (
                <S.ScrollerButton $isReversed onClick={onScrollerClick}>
                  <ChevronDown />
                </S.ScrollerButton>
              )}

              <S.Tabs ref={tabsRef}>
                {tabItems.map(tab => (
                  <S.Tab
                    key={tab.value}
                    data-selected={selectedAdStatus === tab.value}
                    href={ROUTES.profileAds(tab.value)}
                    appearance={
                      selectedAdStatus === tab.value ? 'primary' : 'gray'
                    }
                    isRounded
                    withKeywordsSource={false}
                  >
                    {tab.name}
                    <S.TabCount>
                      {isAdsCountersLoading ? (
                        <S.TabCountSkeleton />
                      ) : (
                        `(${adStatusCounts[tab.value]})`
                      )}
                    </S.TabCount>
                  </S.Tab>
                ))}
              </S.Tabs>

              {hasScroller && !isScrolled && (
                <S.ScrollerButton onClick={onScrollerClick}>
                  <ChevronDown />
                </S.ScrollerButton>
              )}
            </S.TabsWrapper>

            {isArchiveTabSelected && (
              <S.ArchiveInfoBox>
                {t('pages.cabinet.archive_ads_description')}
              </S.ArchiveInfoBox>
            )}

            <Ads
              selectedAdStatus={selectedAdStatus}
              actionHandlers={actionHandlers}
              onArchiveClick={openArchiveAdFlow}
            />
          </>
        )}

        <MyAdsPromotionFlow
          ad={promotionAd}
          isOpen={!!promotionAd}
          onClose={closePromotionFlow}
          onSuccessPayment={handlePromotionSuccess}
        />

        <DeleteAdModal
          submit={handleRemoveAdConfirm}
          toggle={closeRemoveAdFlow}
          isOpen={isRemoveAdFlowOpen}
          reasons={deleteReasons}
        />

        <WarningModal
          dataTestId={AdPageTestIds.settings.editPendingPopup}
          isOpen={isEditPendingApprovalOpen}
          onClose={closeEditPendingApproval}
          title={t('pages.ad.edit_pending_approval')}
          description={t('pages.ad.edit_pending_approval_description')}
        />

        <WarningModal
          dataTestId={AdPageTestIds.statusBanner.payByBalancePopup}
          isOpen={isPayByBalanceOpen}
          onClose={closePayByBalanceFlow}
          title={t('pages.ad.pay_by_balance')}
          description={t('pages.ad.pay_by_balance_description', {
            categoryName: payByBalanceAd?.category?.name,
          })}
          actions={
            <>
              <Button
                data-testid={CommonTestIds.popup.actionLeft}
                appearance="secondaryOutlined"
                isFullWidth
                borderRadius={14}
                disabled={isPayByBalanceLoading}
                onClick={closePayByBalanceFlow}
              >
                {t('common.deny')}
              </Button>
              <Button
                data-testid={CommonTestIds.popup.actionRight}
                appearance="secondary"
                isFullWidth
                borderRadius={14}
                isLoading={isPayByBalanceLoading}
                disabled={isPayByBalanceLoading}
                onClick={confirmPayByBalance}
              >
                {t('common.confirm')}
              </Button>
            </>
          }
        />

        <WarningModal
          isOpen={isArchiveAdFlowOpen}
          onClose={closeArchiveAdFlow}
          title={t('pages.cabinet.archive_ad_question')}
          description={t('pages.cabinet.archive_ad_popup_text')}
          actions={
            <>
              <Button
                data-testid={CommonTestIds.popup.actionLeft}
                appearance="secondaryOutlined"
                isFullWidth
                borderRadius={14}
                disabled={isArchiveAdLoading}
                onClick={closeArchiveAdFlow}
              >
                {t('common.decline')}
              </Button>
              <Button
                data-testid={CommonTestIds.popup.actionRight}
                appearance="secondary"
                isFullWidth
                borderRadius={14}
                isLoading={isArchiveAdLoading}
                disabled={isArchiveAdLoading}
                onClick={handleArchiveAdConfirm}
              >
                {t('common.continue')}
              </Button>
            </>
          }
        />
      </S.ContentCard>

      <SuccessPaymentStatusModal />
      <MyAdsBulkPromotionSuccessModal
        payment={bulkPromotionPaymentReceipt}
        onClose={closeBulkPromotionPaymentReceipt}
      />
    </>
  );
};
